/**
 * Service for managing preset expenses in local storage
 */

const PRESET_EXPENSES_KEY = 'sweetgrass_preset_expenses';

export const presetExpensesService = {
  /**
   * Get all preset expenses from local storage
   */
  getPresetExpenses: () => {
    try {
      const stored = localStorage.getItem(PRESET_EXPENSES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading preset expenses:', error);
      return [];
    }
  },

  /**
   * Save preset expenses to local storage
   */
  savePresetExpenses: (expenses) => {
    try {
      localStorage.setItem(PRESET_EXPENSES_KEY, JSON.stringify(expenses));
      return true;
    } catch (error) {
      console.error('Error saving preset expenses:', error);
      return false;
    }
  },

  /**
   * Add a new preset expense
   */
  addPresetExpense: (expense) => {
    const expenses = presetExpensesService.getPresetExpenses();
    const newExpense = {
      id: Date.now().toString(),
      name: expense.name.trim(),
      amount: parseFloat(expense.amount),
      createdAt: new Date().toISOString()
    };
    
    expenses.push(newExpense);
    return presetExpensesService.savePresetExpenses(expenses) ? newExpense : null;
  },

  /**
   * Update an existing preset expense
   */
  updatePresetExpense: (id, updates) => {
    const expenses = presetExpensesService.getPresetExpenses();
    const index = expenses.findIndex(expense => expense.id === id);
    
    if (index !== -1) {
      expenses[index] = {
        ...expenses[index],
        name: updates.name?.trim() || expenses[index].name,
        amount: updates.amount !== undefined ? parseFloat(updates.amount) : expenses[index].amount,
        updatedAt: new Date().toISOString()
      };
      
      return presetExpensesService.savePresetExpenses(expenses) ? expenses[index] : null;
    }
    
    return null;
  },

  /**
   * Delete a preset expense
   */
  deletePresetExpense: (id) => {
    const expenses = presetExpensesService.getPresetExpenses();
    const filteredExpenses = expenses.filter(expense => expense.id !== id);
    return presetExpensesService.savePresetExpenses(filteredExpenses);
  },

  /**
   * Get a single preset expense by ID
   */
  getPresetExpense: (id) => {
    const expenses = presetExpensesService.getPresetExpenses();
    return expenses.find(expense => expense.id === id) || null;
  },

  /**
   * Convert preset expenses to report expenses format
   */
  convertPresetsToExpenses: (presetIds, date = new Date()) => {
    const allPresets = presetExpensesService.getPresetExpenses();
    const selectedPresets = allPresets.filter(preset => presetIds.includes(preset.id));
    
    return selectedPresets.map(preset => ({
      id: `preset_${preset.id}_${Date.now()}`, // Unique ID for the report
      description: preset.name,
      amount: preset.amount,
      date: typeof date === 'string' ? date : format(date, 'yyyy-MM-dd'),
      isNew: true,
      isFromPreset: true,
      presetId: preset.id
    }));
  }
};

// Helper function for date formatting
const format = (date, formatStr) => {
  if (formatStr === 'yyyy-MM-dd') {
    return date.toISOString().split('T')[0];
  }
  return date.toLocaleDateString();
};